"use client";

import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/utils/utils";
import { styleDrivers } from "@/utils/componentStyle";

// ============================================
// TYPES
// ============================================
export interface TreeMenuItem {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Optional icon */
  icon?: React.ReactNode;
  /** Child items */
  children?: TreeMenuItem[];
  /** Link href */
  href?: string;
  /** Disabled state */
  isDisabled?: boolean;
}

export interface TreeMenuProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> {
  /** Menu items */
  items: TreeMenuItem[];
  /** Currently selected item ID */
  selectedId?: string;
  /** Expanded item IDs */
  expandedIds?: string[];
  /** Called when an item is selected */
  onSelect?: (item: TreeMenuItem) => void;
  /** Called when an item is expanded/collapsed */
  onExpand?: (id: string, expanded: boolean) => void;
  /** Expand all items by default */
  defaultExpandAll?: boolean;
  /** Show icons */
  showIcons?: boolean;
  /** Indentation per level in pixels */
  indent?: number;
}

// ============================================
// TREE ITEM COMPONENT
// ============================================
interface TreeItemProps {
  item: TreeMenuItem;
  level: number;
  selectedId?: string;
  expandedIds: Set<string>;
  onSelect?: (item: TreeMenuItem) => void;
  onToggle: (id: string) => void;
  showIcons: boolean;
  indent: number;
}

const TreeItem: React.FC<TreeItemProps> = ({
  item,
  level,
  selectedId,
  expandedIds,
  onSelect,
  onToggle,
  showIcons,
  indent,
}) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedIds.has(item.id);
  const isSelected = selectedId === item.id;

  const handleClick = () => {
    if (item.isDisabled) return;

    if (hasChildren) {
      onToggle(item.id);
    }
    onSelect?.(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (item.isDisabled) return;

    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    } else if (e.key === "ArrowRight" && hasChildren && !isExpanded) {
      e.preventDefault();
      onToggle(item.id);
    } else if (e.key === "ArrowLeft" && hasChildren && isExpanded) {
      e.preventDefault();
      onToggle(item.id);
    }
  };

  const paddingLeft = level * indent;

  const content = item.href ? (
    <a
      href={item.href}
      className={cn(
        styleDrivers.treeMenu.itemContent,
        isSelected && styleDrivers.treeMenu.itemActive,
        item.isDisabled && styleDrivers.treeMenu.itemDisabled
      )}
      style={{ paddingLeft }}
      onClick={(e) => {
        if (hasChildren) {
          e.preventDefault();
        }
        handleClick();
      }}
      aria-current={isSelected ? "page" : undefined}
    >
      {hasChildren && (
        <ChevronRight
          className={cn(
            styleDrivers.treeMenu.expandIcon,
            isExpanded && "rotate-90"
          )}
        />
      )}
      {!hasChildren && showIcons && <span className="w-4" />}
      {showIcons && item.icon && (
        <span className={styleDrivers.treeMenu.icon}>{item.icon}</span>
      )}
      <span className={styleDrivers.treeMenu.label}>{item.label}</span>
    </a>
  ) : (
    <div
      role="treeitem"
      tabIndex={item.isDisabled ? -1 : 0}
      className={cn(
        styleDrivers.treeMenu.itemContent,
        isSelected && styleDrivers.treeMenu.itemActive,
        item.isDisabled && styleDrivers.treeMenu.itemDisabled
      )}
      style={{ paddingLeft }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-selected={isSelected}
      aria-expanded={hasChildren ? isExpanded : undefined}
      aria-disabled={item.isDisabled}
    >
      {hasChildren && (
        <ChevronRight
          className={cn(
            styleDrivers.treeMenu.expandIcon,
            isExpanded && "rotate-90"
          )}
        />
      )}
      {!hasChildren && showIcons && <span className="w-4" />}
      {showIcons && item.icon && (
        <span className={styleDrivers.treeMenu.icon}>{item.icon}</span>
      )}
      <span className={styleDrivers.treeMenu.label}>{item.label}</span>
    </div>
  );

  return (
    <div className={styleDrivers.treeMenu.item}>
      {content}
      {hasChildren && isExpanded && (
        <div className={styleDrivers.treeMenu.children} role="group">
          {item.children!.map((child) => (
            <TreeItem
              key={child.id}
              item={child}
              level={level + 1}
              selectedId={selectedId}
              expandedIds={expandedIds}
              onSelect={onSelect}
              onToggle={onToggle}
              showIcons={showIcons}
              indent={indent}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// TREE MENU COMPONENT
// ============================================
const TreeMenu = React.forwardRef<HTMLDivElement, TreeMenuProps>(
  (
    {
      className,
      items,
      selectedId,
      expandedIds: controlledExpandedIds,
      onSelect,
      onExpand,
      defaultExpandAll = false,
      showIcons = true,
      indent = 16,
      ...props
    },
    ref
  ) => {
    // Collect all IDs for defaultExpandAll
    const getAllIds = (items: TreeMenuItem[]): string[] => {
      return items.reduce<string[]>((acc, item) => {
        acc.push(item.id);
        if (item.children) {
          acc.push(...getAllIds(item.children));
        }
        return acc;
      }, []);
    };

    const [internalExpandedIds, setInternalExpandedIds] = React.useState<Set<string>>(
      () => new Set(defaultExpandAll ? getAllIds(items) : [])
    );

    const isControlled = controlledExpandedIds !== undefined;
    const expandedIds = isControlled
      ? new Set(controlledExpandedIds)
      : internalExpandedIds;

    const handleToggle = (id: string) => {
      const newExpanded = !expandedIds.has(id);

      if (!isControlled) {
        setInternalExpandedIds((prev) => {
          const next = new Set(prev);
          if (newExpanded) {
            next.add(id);
          } else {
            next.delete(id);
          }
          return next;
        });
      }

      onExpand?.(id, newExpanded);
    };

    return (
      <div
        ref={ref}
        data-slot="tree-menu"
        role="tree"
        className={cn(styleDrivers.treeMenu.root, className)}
        {...props}
      >
        {items.map((item) => (
          <TreeItem
            key={item.id}
            item={item}
            level={0}
            selectedId={selectedId}
            expandedIds={expandedIds}
            onSelect={onSelect}
            onToggle={handleToggle}
            showIcons={showIcons}
            indent={indent}
          />
        ))}
      </div>
    );
  }
);

TreeMenu.displayName = "TreeMenu";

// ============================================
// EXPORTS
// ============================================
export { TreeMenu };
